import React, { useEffect, useState } from 'react';
import { IoAddOutline, IoChevronDown, IoChevronUp, IoStopCircleOutline } from 'react-icons/io5';
import useResearchRuns from '../hooks/useResearchRuns';
import LiveResearchView, { ResearchStatusChip } from './LiveResearchView';
import './ResearchToolTab.css';

function formatRunDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function StartRunForm({ starting, startError, onStart }) {
  const [open, setOpen] = useState(false);
  const [objective, setObjective] = useState('');
  const [name, setName] = useState('');
  const [startUrl, setStartUrl] = useState('');
  const [useResidentialProxy, setUseResidentialProxy] = useState(true);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!objective.trim() || starting) return;
    const run = await onStart({
      objective: objective.trim(),
      name: name.trim() || undefined,
      startUrl: startUrl.trim() || undefined,
      useResidentialProxy,
    });
    if (run) {
      setObjective('');
      setName('');
      setStartUrl('');
      setUseResidentialProxy(true);
      setOpen(false);
    }
  };

  return (
    <div className="research-start-card">
      <button type="button" className="research-start-toggle" onClick={() => setOpen((value) => !value)}>
        <span><IoAddOutline size={15} /> New research</span>
        {open ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />}
      </button>
      {open ? (
        <form className="research-start-form" onSubmit={handleSubmit}>
          <label className="research-form-label" htmlFor="research-objective">Objective</label>
          <textarea
            id="research-objective"
            value={objective}
            onChange={(event) => setObjective(event.target.value)}
            placeholder="What should the agent research? Be specific — it starts with no other context."
            rows={3}
            required
          />
          <label className="research-form-label" htmlFor="research-name">Name (optional)</label>
          <input
            id="research-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Short display name"
          />
          <label className="research-form-label" htmlFor="research-url">Start URL (optional)</label>
          <input
            id="research-url"
            type="url"
            value={startUrl}
            onChange={(event) => setStartUrl(event.target.value)}
            placeholder="https://…"
          />
          <label className="research-form-checkbox">
            <input
              type="checkbox"
              checked={useResidentialProxy}
              onChange={(event) => setUseResidentialProxy(event.target.checked)}
            />
            <span>
              Use residential proxy
              <small>On by default — avoids bot blocks. Uncheck to save units on friendly sites.</small>
            </span>
          </label>
          {startError ? <div className="research-start-error">{startError}</div> : null}
          <button type="submit" className="research-start-submit" disabled={starting || !objective.trim()}>
            {starting ? 'Starting…' : 'Start research'}
          </button>
        </form>
      ) : null}
    </div>
  );
}

export default function ResearchToolTab() {
  const { runs, loading, error, starting, startError, startRun, cancelRun } = useResearchRuns({
    enabled: true,
  });
  const [selectedRunId, setSelectedRunId] = useState(null);

  // Auto-select the newest run on first load.
  useEffect(() => {
    if (!selectedRunId && runs.length > 0) {
      setSelectedRunId(runs[0].run_id);
    }
  }, [runs, selectedRunId]);

  const handleStart = async (input) => {
    const run = await startRun(input);
    if (run?.run_id) setSelectedRunId(run.run_id);
    return run;
  };

  return (
    <div className="research-tab">
      <div className="research-tab-list">
        <StartRunForm starting={starting} startError={startError} onStart={handleStart} />
        {loading ? <div className="research-tab-empty">Loading runs…</div> : null}
        {error ? <div className="research-tab-empty research-tab-error">{error}</div> : null}
        {!loading && !error && runs.length === 0 ? (
          <div className="research-tab-empty">
            No research runs yet. Start one above, or ask Atlas to research something on the live web.
          </div>
        ) : null}
        <div className="research-run-list">
          {runs.map((run) => {
            const isActive = run.status === 'queued' || run.status === 'running';
            return (
              <div
                key={run.run_id}
                role="button"
                tabIndex={0}
                className={`research-run-row${run.run_id === selectedRunId ? ' selected' : ''}`}
                onClick={() => setSelectedRunId(run.run_id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') setSelectedRunId(run.run_id);
                }}
              >
                <div className="research-run-row-top">
                  <span className="research-run-name" title={run.objective}>{run.name || run.run_id}</span>
                  <ResearchStatusChip status={run.status} stalled={run.stalled} />
                </div>
                <div className="research-run-row-bottom">
                  <span className="research-run-meta">
                    {formatRunDateTime(run.started_at || run.created_at)}
                    {' · '}
                    {run.source === 'atlas' ? 'from chat' : 'manual'}
                    {run.step_count ? ` · ${run.step_count} steps` : ''}
                  </span>
                  {isActive ? (
                    <button
                      type="button"
                      className="research-run-cancel"
                      title="Stop this run"
                      onClick={(event) => {
                        event.stopPropagation();
                        cancelRun(run.run_id);
                      }}
                    >
                      <IoStopCircleOutline size={15} />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="research-tab-detail">
        {selectedRunId ? (
          <LiveResearchView key={selectedRunId} runId={selectedRunId} variant="tab" />
        ) : (
          <div className="research-tab-detail-empty">Select a run or start a new one to watch it live.</div>
        )}
      </div>
    </div>
  );
}
